/**
 * StaffManager — handles StaffEng.json / StaffAlb.json
 * Data shape: { staff: { title, description, list: [{ name, position, photo, description, linkedin, social }] } }
 */
import React, { Component } from 'react';
import AdminLayout from '../AdminLayout.jsx';
import { fetchDataFile, saveDataFile, uploadPhotoFile } from '../../../APICall/Api.jsx';

const FILES = { eng: 'StaffEng.json', alb: 'StaffAlb.json' };

const EMPTY = { name: '', position: '', photo: '', description: '', linkedin: '' };

class StaffManager extends Component {
  state = {
    lang: 'eng',
    rawData: null,
    staffMeta: { title: '', description: '' },
    items: [],
    loading: true,
    saving: false,
    error: null,
    modal: null,        // null | 'add' | 'edit' | 'meta'
    editIndex: null,
    formData: { ...EMPTY },
    metaForm: { title: '', description: '' },
    uploadingImg: false,
    savedMsg: '',
  };

  componentDidMount() { this.loadData(); }

  loadData = async () => {
    const { lang } = this.state;
    this.setState({ loading: true, error: null });
    try {
      const data = await fetchDataFile(FILES[lang]);
      const staff = data.staff || {};
      this.setState({
        rawData: data,
        staffMeta: { title: staff.title || '', description: staff.description || '' },
        items: staff.list || [],
        loading: false,
      });
    } catch (e) {
      this.setState({ error: 'Failed to load staff.', loading: false });
    }
  };

  switchLang = (lang) => this.setState({ lang, items: [], loading: true, rawData: null }, this.loadData);

  saveData = async (newItems, meta) => {
    this.setState({ saving: true });
    try {
      const { rawData, lang, staffMeta } = this.state;
      const usedMeta = meta || staffMeta;
      const payload = {
        ...rawData,
        staff: { ...usedMeta, list: newItems },
      };
      await saveDataFile(FILES[lang], payload);
      this.setState({
        items: newItems,
        staffMeta: usedMeta,
        saving: false,
        savedMsg: 'Saved!',
      });
      setTimeout(() => this.setState({ savedMsg: '' }), 3000);
    } catch (e) {
      this.setState({ saving: false, error: 'Save failed. Check your admin token.' });
    }
  };

  openAdd  = () => this.setState({ modal: 'add', formData: { ...EMPTY }, editIndex: null });
  openEdit = (idx) => this.setState({ modal: 'edit', editIndex: idx, formData: { ...this.state.items[idx] } });
  openMeta = () => this.setState({ modal: 'meta', metaForm: { ...this.state.staffMeta } });
  closeModal = () => this.setState({ modal: null });

  handleField = (key, val) =>
    this.setState((s) => ({ formData: { ...s.formData, [key]: val } }));

  handleMetaField = (key, val) =>
    this.setState((s) => ({ metaForm: { ...s.metaForm, [key]: val } }));

  handleImgUpload = async (file) => {
    this.setState({ uploadingImg: true });
    try {
      const res = await uploadPhotoFile(file);
      if (res.path) {
        const url = `https://buildgreenalbania.org/${res.path}`;
        this.setState((s) => ({
          formData: { ...s.formData, photo: url },
          uploadingImg: false,
        }));
      }
    } catch (e) {
      this.setState({ uploadingImg: false });
    }
  };

  handleSave = () => {
    const { items, editIndex, formData, modal } = this.state;
    const newItems = modal === 'add'
      ? [formData, ...items]
      : items.map((it, i) => (i === editIndex ? formData : it));
    this.saveData(newItems);
    this.closeModal();
  };

  handleMetaSave = () => {
    this.saveData(this.state.items, this.state.metaForm);
    this.closeModal();
  };

  handleDelete = (idx) => {
    if (!window.confirm('Delete this staff member?')) return;
    const newItems = this.state.items.filter((_, i) => i !== idx);
    this.saveData(newItems);
  };

  render() {
    const { lang, items, loading, saving, modal, formData, metaForm, uploadingImg, savedMsg, error, staffMeta } = this.state;
    const { history, location } = this.props;

    return (
      <AdminLayout title="Manage Staff" history={history} location={location}>
        {/* Toolbar */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <div className="bgg-lang-tabs">
              {['eng', 'alb'].map((l) => (
                <button key={l} className={`bgg-lang-tab${lang === l ? ' active' : ''}`} onClick={() => this.switchLang(l)}>
                  {l.toUpperCase()}
                </button>
              ))}
            </div>
            {savedMsg && <span style={{ color: '#2e7d32', fontWeight: 600 }}>✓ {savedMsg}</span>}
            {error && <span style={{ color: '#e53935' }}>{error}</span>}
          </div>
          <div style={{ display: 'flex', gap: 10 }}>
            <button className="bgg-btn bgg-btn-outline bgg-btn-sm" onClick={this.openMeta}>
              <i className="fas fa-cog" /> Section Settings
            </button>
            <button className="bgg-btn bgg-btn-primary" onClick={this.openAdd}>
              <i className="fas fa-plus" /> Add Member
            </button>
          </div>
        </div>

        {/* Section meta info */}
        {!loading && (
          <div className="bgg-admin-card" style={{ marginBottom: 20 }}>
            <div className="bgg-admin-card-header">
              <h2>Section Info</h2>
            </div>
            <div style={{ padding: '12px 24px', color: '#555', fontSize: '0.9rem' }}>
              <strong>Title:</strong> {staffMeta.title || <em style={{ color: '#bbb' }}>Not set</em>}<br />
              <strong>Description:</strong> {staffMeta.description || <em style={{ color: '#bbb' }}>Not set</em>}
            </div>
          </div>
        )}

        {/* Table */}
        <div className="bgg-admin-card">
          <div className="bgg-admin-card-header">
            <h2>Staff Members ({loading ? '…' : items.length}) — {lang.toUpperCase()}</h2>
          </div>
          {loading
            ? <div style={{ padding: 32, textAlign: 'center', color: '#8a8aaa' }}>Loading…</div>
            : (
              <div style={{ overflowX: 'auto' }}>
                <table className="bgg-admin-table">
                  <thead>
                    <tr>
                      <th>#</th>
                      <th>Photo</th>
                      <th>Name</th>
                      <th>Position</th>
                      <th>LinkedIn</th>
                      <th style={{ width: 120 }}>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {items.map((item, idx) => (
                      <tr key={idx}>
                        <td style={{ color: '#aaa', width: 40 }}>{idx + 1}</td>
                        <td>
                          {item.photo || item.img
                            ? <img src={item.photo || item.img} alt="" style={{ width: 40, height: 40, borderRadius: '50%', objectFit: 'cover' }} />
                            : <div style={{ width: 40, height: 40, borderRadius: '50%', background: '#e8f5e9', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                <i className="fas fa-user" style={{ color: '#66bb6a' }} />
                              </div>}
                        </td>
                        <td><strong>{item.name}</strong></td>
                        <td style={{ fontSize: '0.85rem', color: '#666' }}>{item.position}</td>
                        <td style={{ fontSize: '0.82rem' }}>
                          {(item.linkedin || item.social)
                            ? <a href={item.linkedin || item.social} target="_blank" rel="noopener noreferrer" style={{ color: '#1976d2' }}>
                                <i className="fab fa-linkedin" />
                              </a>
                            : <span style={{ color: '#ccc' }}>–</span>}
                        </td>
                        <td>
                          <div className="bgg-table-actions">
                            <button className="bgg-btn bgg-btn-outline bgg-btn-sm" onClick={() => this.openEdit(idx)}>
                              <i className="fas fa-edit" />
                            </button>
                            <button className="bgg-btn bgg-btn-danger bgg-btn-sm" onClick={() => this.handleDelete(idx)}>
                              <i className="fas fa-trash" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                {items.length === 0 && (
                  <div style={{ padding: 32, textAlign: 'center', color: '#aaa' }}>
                    No staff members yet. Click "Add Member" to create one.
                  </div>
                )}
              </div>
            )}
        </div>

        {/* Add/Edit Member Modal */}
        {(modal === 'add' || modal === 'edit') && (
          <div className="bgg-modal-overlay" onClick={this.closeModal}>
            <div className="bgg-modal" onClick={(e) => e.stopPropagation()}>
              <div className="bgg-modal-header">
                <h3>{modal === 'add' ? 'Add Staff Member' : 'Edit Staff Member'}</h3>
                <button className="bgg-modal-close" onClick={this.closeModal}>×</button>
              </div>
              <div className="bgg-modal-body">
                {[
                  { key: 'name',     label: 'Full Name' },
                  { key: 'position', label: 'Position / Role' },
                  { key: 'linkedin', label: 'LinkedIn URL' },
                ].map(({ key, label }) => (
                  <div className="bgg-form-group" key={key}>
                    <label className="bgg-form-label">{label}</label>
                    <input
                      className="bgg-form-input"
                      type="text"
                      value={formData[key] || ''}
                      onChange={(e) => this.handleField(key, e.target.value)}
                    />
                  </div>
                ))}

                {/* Photo field */}
                <div className="bgg-form-group">
                  <label className="bgg-form-label">Photo</label>
                  <input
                    className="bgg-form-input"
                    type="text"
                    value={formData.photo || ''}
                    onChange={(e) => this.handleField('photo', e.target.value)}
                    placeholder="Paste image URL or upload below"
                  />
                  <div style={{ marginTop: 8, display: 'flex', alignItems: 'center', gap: 10 }}>
                    <input
                      type="file"
                      accept="image/*"
                      style={{ fontSize: '0.82rem' }}
                      onChange={(e) => e.target.files[0] && this.handleImgUpload(e.target.files[0])}
                    />
                    {uploadingImg && <span style={{ color: '#8a8aaa', fontSize: '0.82rem' }}>Uploading…</span>}
                  </div>
                  {formData.photo && (
                    <img src={formData.photo} alt="" style={{ marginTop: 8, height: 80, width: 80, borderRadius: '50%', objectFit: 'cover' }} />
                  )}
                </div>

                {/* Description */}
                <div className="bgg-form-group">
                  <label className="bgg-form-label">Bio / Description</label>
                  <textarea
                    className="bgg-form-textarea"
                    value={formData.description || ''}
                    onChange={(e) => this.handleField('description', e.target.value)}
                    rows={4}
                  />
                </div>
              </div>
              <div className="bgg-modal-footer">
                <button className="bgg-btn bgg-btn-outline" onClick={this.closeModal}>Cancel</button>
                <button className="bgg-btn bgg-btn-primary" onClick={this.handleSave} disabled={saving}>
                  {saving ? 'Saving…' : 'Save'}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Section Meta Modal */}
        {modal === 'meta' && (
          <div className="bgg-modal-overlay" onClick={this.closeModal}>
            <div className="bgg-modal" onClick={(e) => e.stopPropagation()}>
              <div className="bgg-modal-header">
                <h3>Section Settings</h3>
                <button className="bgg-modal-close" onClick={this.closeModal}>×</button>
              </div>
              <div className="bgg-modal-body">
                <div className="bgg-form-group">
                  <label className="bgg-form-label">Section Title</label>
                  <input
                    className="bgg-form-input"
                    type="text"
                    value={metaForm.title || ''}
                    onChange={(e) => this.handleMetaField('title', e.target.value)}
                  />
                </div>
                <div className="bgg-form-group">
                  <label className="bgg-form-label">Section Description</label>
                  <textarea
                    className="bgg-form-textarea"
                    value={metaForm.description || ''}
                    onChange={(e) => this.handleMetaField('description', e.target.value)}
                    rows={3}
                  />
                </div>
              </div>
              <div className="bgg-modal-footer">
                <button className="bgg-btn bgg-btn-outline" onClick={this.closeModal}>Cancel</button>
                <button className="bgg-btn bgg-btn-primary" onClick={this.handleMetaSave} disabled={saving}>
                  {saving ? 'Saving…' : 'Save'}
                </button>
              </div>
            </div>
          </div>
        )}
      </AdminLayout>
    );
  }
}

export default StaffManager;



// WEBPACK FOOTER //
// ./src/views/Admin/managers/StaffManager.jsx